/**
 * Activation surfaces are keyed by the audience's RudderStack source id, not its Lookout id. This maps
 * one to whether its Lookout audience needs repair (ACT2-1286), so Edit activation can be disabled with
 * the reason (Create is gated on the audience page itself). It reads `audience.list` only while the
 * repair flag is on, so with the flag off nothing new is fetched and every answer is `'ok'`. Until the
 * list has answered the answer is `'unknown'`, and `'failed'` when it could not be read; the caller keeps
 * Edit off in both rather than offering a form the server might refuse.
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

/** Why Edit is off when the audience states could not be read. */
export const AUDIENCE_STATE_UNREADABLE = "Couldn't check this audience. Reload to try again.";

export type AudienceRepairAnswer = 'ok' | 'needs_repair' | 'unknown' | 'failed';

export function useAudienceNeedsRepairBySource(): (rsSourceId: string) => AudienceRepairAnswer {
  const orpc = useOrpc();
  const { isAccountRepairEnabled } = useWorkspace();
  const { data, isError } = useQuery({
    ...orpc.audience.list.queryOptions({ input: {} }),
    enabled: isAccountRepairEnabled,
  });
  const broken = useMemo(() => {
    const ids = new Set<string>();
    for (const audience of data?.audiences ?? []) {
      const { state } = audience.accountRepair;
      if (audience.rsSourceId && (state === 'needs_repair' || state === 'unassigned')) ids.add(audience.rsSourceId);
    }
    return ids;
  }, [data?.audiences]);
  const known = !isAccountRepairEnabled || data !== undefined;
  const failed = isAccountRepairEnabled && data === undefined && isError;
  // Flag off answers 'ok' before the set is read: a disabled query keeps its cached data, and an
  // `audience.list` cached while the flag was on (here or on the audience list) would otherwise keep
  // Edit disabled with "Repair this audience first" while Repair itself is unavailable.
  return useCallback(
    (rsSourceId: string): AudienceRepairAnswer =>
      !isAccountRepairEnabled
        ? 'ok'
        : failed
          ? 'failed'
          : !known
            ? 'unknown'
            : broken.has(rsSourceId)
              ? 'needs_repair'
              : 'ok',
    [isAccountRepairEnabled, broken, known, failed],
  );
}
